import { View } from "@react-pdf/renderer";

import { getFullName } from "utils/common";
import { frontendLog } from "utils/debug.utils";

import { PdfDocumentStaffSignature } from "../footer-v2/pdf-document-staff-signature.type";
import { usePdfPageChrome } from "../pdf-page-chrome";
import SignatureSlot from "./signature-slot.component";
import SignedAtSlot from "./signed-at-slot.component";

// ENG-2277: a slot prints its signature image only with a date (SignatureSlot), so the date is what
// says a slot was signed. A signature may carry its own: a date for a slot signed that day, null for
// one nobody signed, so one person signing never prints another's saved signature. A signature that
// carries none is dated with the footer, as every caller but the custom print form's is.
export type PdfDocumentFooterSignature = PdfDocumentStaffSignature & {
  signedAtDate?: string | null;
};

type PdfDocumentFooterReportV3Props = {
  signedAtDate?: string;
  signatures: PdfDocumentFooterSignature[];
  // Gives each label the slot's full content width so a long one wraps sensibly instead
  // of climbing into the signature image. Opt-in: callers that predate SN-19170 (chart
  // notes, the custom print form) render exactly as they did before.
  constrainLabelWidth?: boolean;
};

const getSignatureFullName = (signature?: PdfDocumentStaffSignature) =>
  signature?.staffMember
    ? getFullName({
        firstName: signature?.staffMember?.first_name || "",
        lastName: signature?.staffMember?.last_name || "",
        title: signature?.staffMember?.title,
        suffix: signature?.staffMember?.suffix
      })
    : "";

// Three signatories fit on a report that is directly supervised AND cosigned: the
// treating provider, the supervisor, and the cosigner (SN-19170). The wide fixed-width
// slot used for one or two signatures cannot hold a third, so past two the slots switch
// to an evenly divided compact layout. One- and two-signature footers — every chart note
// and every unsupervised report — keep the original layout untouched.
const COMPACT_LAYOUT_THRESHOLD = 2;

// The wide slot's 500px less its 110px left padding.
const WIDE_SLOT_CONTENT_WIDTH_PX = 390;

export default function PdfDocumentFooterReportV3({
  signedAtDate,
  signatures,
  constrainLabelWidth = false
}: PdfDocumentFooterReportV3Props) {
  const [providerSignature, ...additionalSignatures] = signatures;

  // The primary slot renders unconditionally, even with no staff member behind it, so a
  // report whose appointment has no provider still prints a signature line to sign by
  // hand. Every slot after it needs both a label and a name or there is nothing to show.
  const namedAdditionalSignatures = additionalSignatures.filter(
    (signature) =>
      Boolean(signature?.label) && Boolean(getSignatureFullName(signature))
  );

  const renderedSignatures = [providerSignature, ...namedAdditionalSignatures];
  const signaturesCount = renderedSignatures.length;
  const isCompact = signaturesCount > COMPACT_LAYOUT_THRESHOLD;

  frontendLog(["PdfDocumentFooterReportV3", { signaturesCount }], "components");

  // ENG-2277: lengths are a legacy page's px, scaled on a Form's page, and the footer starts at the
  // page's left margin (see pdf-page-chrome).
  const { scale, footerLeft } = usePdfPageChrome();

  return (
    <View
      wrap={false}
      style={{
        width: "100%",
        position: "absolute",
        bottom: 56 * scale,
        left: footerLeft
      }}
    >
      <View
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "flex-end"
        }}
      >
        <SignedAtSlot signedAtDate={signedAtDate} />
        <View
          style={{
            width: "84%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end"
          }}
        >
          {renderedSignatures.map((signature, index) => (
            <SignatureSlot
              // Signatories are a fixed, ordered set (provider, supervisor, cosigner),
              // so the position is the identity.
              key={`${index}-${signature?.label ?? ""}`}
              signedAtDate={
                signature?.signedAtDate === undefined
                  ? signedAtDate
                  : (signature.signedAtDate ?? undefined)
              }
              signatureImgUrl={signature?.staffMember?.signature}
              signatureSize={signature?.signatureSize}
              signatureLabel={signature?.label}
              providerFullName={getSignatureFullName(signature)}
              licenseNumber={signature?.licenseNumber}
              compact={isCompact}
              labelWidth={
                constrainLabelWidth && !isCompact
                  ? `${WIDE_SLOT_CONTENT_WIDTH_PX * scale}px`
                  : undefined
              }
              // Preserves the original two-slot rendering exactly: only the first slot
              // was told how many signatures there were, so only it wrapped its license
              // number onto its own line. In the compact layout every slot wraps.
              signaturesCount={
                isCompact || index === 0 ? signaturesCount : undefined
              }
            />
          ))}
        </View>
      </View>
      <View
        style={{
          width: "100%",
          height: `${3 * scale}px`,
          backgroundColor: "#381337",
          marginTop: `${-7 * scale}px`
        }}
      ></View>
    </View>
  );
}
